import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';

import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { getAdditionalFilterLabel } from 'bundles/enterprise-admin-user-filters/constants';
import type {
  ActivityFilter,
  DateClause,
  EnrichedData,
  PrimaryFilterType,
  UserFilter,
} from 'bundles/enterprise-admin-user-filters/types';
import { formatDate, getContractLabel, getDays } from 'bundles/enterprise-admin-user-filters/utils';
import { assertNever, formatNumber } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const getDaysClause = (days?: number) => (
  <FormattedMessage
    message={_t('in the last {days} {days, plural, =1 {day} other {days}}')}
    days={days ? formatNumber(days) : '-'}
  />
);

export const getDateClause = (dateClause?: DateClause) => {
  const defaultClause = _t('at any time');
  if (!dateClause) {
    return defaultClause;
  } else if (dateClause.type === 'RELATIVE') {
    return getDaysClause(getDays(dateClause));
  } else if (dateClause.after && !dateClause.before) {
    return <FormattedMessage message={_t('after {date}')} date={formatDate(dateClause.after)} />;
  } else if (dateClause.after && dateClause.before) {
    return (
      <FormattedMessage
        message={_t('between {after} and {before}')}
        before={formatDate(dateClause.before)}
        after={formatDate(dateClause.after)}
      />
    );
  } else {
    return defaultClause;
  }
};

export const PrimaryFilterDescription = ({
  filter,
  programJoinDate,
}: {
  filter: PrimaryFilterType;
  programJoinDate?: DateClause;
}) => {
  switch (filter) {
    case 'MEMBER':
      return (
        <li>
          <Typography2 component="p">
            <FormattedMessage
              message={_t('Joined one of these programs {dateClause}')}
              dateClause={getDateClause(programJoinDate)}
            />
          </Typography2>
        </li>
      );
    case 'INVITED':
      return (
        <li data-testid="INVITED">
          <Typography2 component="p">{_t('Invited to one of these programs')}</Typography2>
        </li>
      );

    case 'REMOVED_MEMBER':
      return (
        <li>
          <Typography2 component="p">{_t('Removed from one of these programs')}</Typography2>
        </li>
      );
    case 'MULTI_TYPE_FILTER':
      return null;
    default:
      return assertNever(filter);
  }
};

export const ActivityFilterDescription = ({
  filter,
  enrichedData,
}: {
  filter: ActivityFilter;
  enrichedData: EnrichedData;
}) => {
  const matchingProducts = filter.productIdSelection?.values.map(({ productItemId }) => (
    <li key={productItemId}>
      <Typography2 component="p">
        {enrichedData?.enrichedProducts.find((_) => _.productItemId === productItemId)?.productName ?? _t('Unknown')}
      </Typography2>
    </li>
  ));
  const matchingProductsListItem = (
    <li>
      {matchingProducts ? (
        <React.Fragment>
          <Typography2 component="p">{_t('Matching any of the selected courses / specializations:')}</Typography2>
          <ul>{matchingProducts}</ul>
        </React.Fragment>
      ) : (
        <Typography2 component="p">{_t('Matching all courses / specializations')}</Typography2>
      )}
    </li>
  );
  switch (filter.type) {
    case 'NO_ENROLLMENTS':
      return (
        <li data-testid="NO_ENROLLMENTS">
          <Typography2 component="p">{_t('Never enrolled in a course')}</Typography2>
        </li>
      );

    case 'GENERAL_INACTIVITY':
      return (
        <li>
          <Typography2 component="p">
            <FormattedMessage
              message={_t('Inactive {daysClause}')}
              daysClause={getDaysClause(getDays(filter?.lastActivityDate))}
            />
          </Typography2>
        </li>
      );

    case 'ENROLLED':
      return (
        <React.Fragment>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('Enrolled {dateClause}')}
                dateClause={getDateClause(filter.enrollmentDate)}
              />
            </Typography2>
          </li>
          {matchingProductsListItem}
        </React.Fragment>
      );

    case 'ENROLLED_BUT_INACTIVE':
      return (
        <React.Fragment>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('Enrolled {dateClause} and inactive {inactivityDateClause}')}
                dateClause={getDateClause(filter.enrollmentDate)}
                inactivityDateClause={getDateClause(filter.lastActivityDate)}
              />
            </Typography2>
          </li>
          {matchingProductsListItem}
        </React.Fragment>
      );

    case 'COMPLETED':
      return (
        <React.Fragment>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('Enrolled {dateClause}')}
                dateClause={getDateClause(filter.enrollmentDate)}
              />
            </Typography2>
          </li>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('At least 1 completion {dateClause}')}
                dateClause={getDateClause(filter.completionDate)}
              />
            </Typography2>
          </li>
          {matchingProductsListItem}
        </React.Fragment>
      );

    case 'PROGRESS_IN_S12N':
      // TODO fix me PROGRESS_IN_S12N is unsupported
      return null;

    case 'ASSIGNED_CONTENT':
      return (
        <li data-testid="ASSIGNED_CONTENT">
          <Typography2 component="p">{_t('Assigned content')}</Typography2>
          <ul>
            {enrichedData?.enrichedProducts.map((product) => (
              <li key={product.productItemId}>
                <Typography2 component="p">{product.productName}</Typography2>
              </li>
            ))}
          </ul>
        </li>
      );

    default:
      return assertNever(filter.type);
  }
};

export const getMatchingPrograms = (canManageOrganization: boolean, programs: string[] = []) => {
  if (programs.length > 0) {
    return (
      <li key="selected-programs">
        <Typography2 component="p">{_t('Matching any of the selected programs:')}</Typography2>
        <ul>
          {programs.map((program) => (
            <li key={program}>
              <Typography2 component="p">{program}</Typography2>
            </li>
          ))}
        </ul>
      </li>
    );
  } else if (canManageOrganization) {
    return (
      <li key="all-programs">
        <Typography2 component="p">{_t('Matching all programs')}</Typography2>
      </li>
    );
  } else {
    return (
      <li key="programs-with-access" data-testid="ALL_PROGRAMS_WITH_ACCESS">
        <Typography2 component="p">{_t('Matching all programs that you have access to')}</Typography2>
      </li>
    );
  }
};

export const UserFilterDescription = ({
  userFilter,
  searchNamesOrEmails,
  enrichedData,
}: {
  userFilter: UserFilter;
  searchNamesOrEmails?: string;
  enrichedData?: EnrichedData;
}) => {
  const { getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const items: JSX.Element[] = [];

  const programIdSelections =
    userFilter.primaryFilter?.type === 'MULTI_TYPE_FILTER'
      ? userFilter.primaryFilter.multiFilter?.programIdSelection?.values
      : userFilter.primaryFilter?.programIdSelection?.values;

  items.push(
    getMatchingPrograms(
      getIsOrganizationDomainAdmin(),
      programIdSelections?.map(
        (id) => enrichedData?.enrichedPrograms.find((_) => _.programId === id)?.programName ?? _t('Unknown')
      )
    )
  );

  if (userFilter.primaryFilter?.type) {
    items.push(
      <PrimaryFilterDescription
        key="primary-filter"
        filter={userFilter.primaryFilter.type}
        programJoinDate={
          userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter.programJoinDate : undefined
        }
      />
    );
  }

  const activityFilter =
    userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter?.activityFilter : undefined;
  const type = activityFilter?.type;
  if (activityFilter && type) {
    items.push(<ActivityFilterDescription key="activity-filter" filter={activityFilter} enrichedData={enrichedData} />);
  }

  userFilter?.additionalAttributeSelections?.forEach((additionalFilter) => {
    if (additionalFilter.values.length === 0) return;
    items.push(
      <li key={`additional-filter-${additionalFilter.attributeName}`}>
        <Typography2 component="p">
          {_t('Selected #{attribute}:', { attribute: getAdditionalFilterLabel(additionalFilter.attributeName) })}
        </Typography2>
        <ul>
          {additionalFilter.values.map((value) => (
            <li key={value}>
              <Typography2 component="p">
                {additionalFilter.attributeName === 'CONTRACT' ? getContractLabel(enrichedData, value) : value}
              </Typography2>
            </li>
          ))}
        </ul>
      </li>
    );
  });

  if (searchNamesOrEmails) {
    items.push(
      <li>
        <Typography2 component="p">
          <FormattedMessage message={_t('Name or email matching: {query}')} query={searchNamesOrEmails} />
        </Typography2>
      </li>
    );
  }

  return <ul>{items}</ul>;
};
